/**
 * PlayerReportsIdentityHeaderView — who the reports below are about (SE-7237).
 *
 * `/players/{id}/reports` renders the reports feature standalone: a heading
 * reading "Reports", three aggregate cards and a list, with no name, club,
 * position or way back to the player. It was a page of numbers about an unnamed
 * subject — a scout arriving from a notification or a shared link had nothing on
 * screen telling them whose career they were reading.
 *
 * Composed rather than reusing the player page's `PlayerIdentityBlock`: that
 * organism is the hero block, and carries watchlist pills, nationality flags,
 * valuation and injury badges, role archetypes and a theme read. Rendering it
 * here would duplicate the player page's header above a list that is one tab of
 * it. This is the compact restatement — enough to identify the subject and get
 * back to them — built from the same atoms and the shared `PlayerAvatar`
 * molecule, so the avatar treatment stays defined in one place.
 * @module features/reports/components/organisms/PlayerReportsIdentityHeader/PlayerReportsIdentityHeaderView
 */
import type { Href } from "expo-router";
import { Link } from "expo-router";
import { ChevronLeft } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PlayerAvatar from "@/components/molecules/PlayerAvatar";
import { TEST_ID_PLAYER_REPORTS_IDENTITY } from "@/e2e/testId";

/** Props for PlayerReportsIdentityHeaderView. */
export interface PlayerReportsIdentityHeaderViewProps {
  /** The player's display name — the subject of every number below. */
  readonly playerName: string;
  /**
   * Club and positions, already joined, or null when neither is known.
   *
   * One line rather than two fields, because a missing club and a missing
   * position each leave a gap that reads as a broken row; joining them lets
   * whichever is known stand on its own.
   */
  readonly subtitle: string | null;
  /** Route back to the player page, already context-expanded. */
  readonly playerHref: Href;
}

/**
 * Renders the compact player identity header above the reports.
 * @param root0 - Component props
 * @param root0.playerName - The player's display name
 * @param root0.subtitle - Club and positions, or null
 * @param root0.playerHref - Route back to the player page
 * @returns The header element.
 */
const PlayerReportsIdentityHeaderView = ({
  playerName,
  subtitle,
  playerHref,
}: PlayerReportsIdentityHeaderViewProps) => (
  <Stack
    space="2"
    testID={TEST_ID_PLAYER_REPORTS_IDENTITY.HEADER}
    className="pb-4"
  >
    {/* An explicit link, not a tappable header: the whole block being pressable
        would make the name look like a control and give no hint where it goes.
        The label names the destination so a reader knows what "back" means when
        they arrived here from a notification rather than from the player page. */}
    <Link asChild href={playerHref}>
      <Button
        testID={TEST_ID_PLAYER_REPORTS_IDENTITY.BACK}
        variant="link"
        size="sm"
        icon={ChevronLeft}
        label="Player page"
        accessibilityLabel={`Back to ${playerName}'s player page`}
        className="self-start"
      />
    </Link>

    <Stack direction="horizontal" space="3" className="items-center">
      <PlayerAvatar playerName={playerName} />

      <Stack space="micro" className="flex-1">
        <Text
          variant="title"
          testID={TEST_ID_PLAYER_REPORTS_IDENTITY.NAME}
          numberOfLines={1}
          className="text-content-primary"
        >
          {playerName}
        </Text>

        {subtitle === null ? null : (
          <Text
            variant="caption"
            numberOfLines={1}
            className="text-content-secondary"
          >
            {subtitle}
          </Text>
        )}
      </Stack>
    </Stack>
  </Stack>
);

PlayerReportsIdentityHeaderView.displayName = "PlayerReportsIdentityHeaderView";

export default memo(PlayerReportsIdentityHeaderView);
